import { useState, useEffect, useRef } from 'react';
import {
  Search, Send, Square, Bot, User, Loader2,
  MessageSquare, ShieldAlert, Plus, Sparkles,
} from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import { buildApiUrl } from '../../services/apiConfig';

const API = (path) => buildApiUrl('NOVA_INGESTION', path);

const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-700',
  high:     'bg-yellow-100 text-yellow-700',
  medium:   'bg-blue-100 text-blue-700',
  low:      'bg-green-100 text-green-700',
};

function DQRulesPanel({ sourceId }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await fetch(API('/llm/dq-rules/list-objects'), { headers: { Authorization: 'Bearer dev-token' } });
      const data = await res.json();
      setRules(data.objects || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRules(); }, [sourceId]);

  const generateRules = async () => {
    if (!sourceId) return;
    setGenerating(true);
    try {
      await fetch(API('/llm/dq-rules/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer dev-token' },
        body: JSON.stringify({ source_id: sourceId }),
      });
      await loadRules();
    } catch { /* ignore */ }
    finally { setGenerating(false); }
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-primary" /> DQ Rules
        </h3>
        <button onClick={generateRules} disabled={generating}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-md text-xs font-medium hover:bg-primaryDark disabled:opacity-50 transition-colors">
          {generating ? <><Loader2 className="h-3 w-3 animate-spin" /> Generating...</> : <><Sparkles className="h-3 w-3" /> Generate Rules</>}
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading...
        </div>
      ) : rules.length === 0 ? (
        <p className="text-sm text-gray-500">No DQ rules found. Generate rules for a source to get started.</p>
      ) : rules.map((obj, i) => (
        <div key={i} className="border border-gray-200 rounded-lg overflow-hidden mb-3">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
            <p className="text-xs font-semibold text-gray-700">{obj.object_path}</p>
          </div>
          {(obj.rules || []).map((rule, j) => (
            <div key={j} className="px-3 py-2 flex items-center justify-between border-b border-gray-100 last:border-0">
              <div>
                <p className="text-sm text-gray-800">{rule.rule_name}</p>
                <p className="text-xs text-gray-400">{rule.rule_type} · {rule.column_name}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rule.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {rule.is_approved ? 'Approved' : 'Draft'}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function InvestigationChat({ investigation, onUpdate }) {
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const chatEndRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [investigation?.messages]);

  const sendMessage = async () => {
    if (!input.trim() || streaming) return;
    const text = input.trim(); setInput('');
    const sid = investigation?.id || `sess-${Date.now()}`;
    const userMsg = { id: Date.now().toString(), role: 'user', parts: [{ kind: 'text', content: text }] };
    const asstMsg = { id: (Date.now() + 1).toString(), role: 'assistant', parts: [{ kind: 'text', content: '' }] };
    onUpdate(inv => ({ ...inv, messages: [...(inv?.messages || []), userMsg, asstMsg] }));
    setStreaming(true);
    try {
      const ctrl = new AbortController(); abortRef.current = ctrl;
      const res = await fetch(API('/transfer/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer dev-token' },
        body: JSON.stringify({ message: text, session_id: sid }),
        signal: ctrl.signal,
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder(); let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n'); buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'text' || evt.type === 'content') {
              onUpdate(inv => {
                const msgs = [...(inv?.messages || [])];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant') {
                  const parts = [...last.parts];
                  const lp = parts[parts.length - 1];
                  if (lp?.kind === 'text') parts[parts.length - 1] = { ...lp, content: lp.content + (evt.content || '') };
                  else parts.push({ kind: 'text', content: evt.content || '' });
                  msgs[msgs.length - 1] = { ...last, parts };
                }
                return { ...inv, messages: msgs };
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch (err) { if (err.name !== 'AbortError') { /* ignore */ } }
    finally { setStreaming(false); }
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const msgs = investigation?.messages || [];
  const lastMsg = msgs[msgs.length - 1];

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50">
        {!msgs.length && (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <div className="p-4 bg-primaryLight rounded-full mb-4">
              <Search className="h-8 w-8 text-primary" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1">Start your investigation</p>
            <p className="text-sm text-gray-500 max-w-sm">Ask about data quality issues, anomalies, schema drift, or anything else about your data.</p>
          </div>
        )}
        {msgs.map((msg) => (
          <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-primary' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
              {msg.role === 'assistant' ? <Bot className="h-4 w-4 text-white" /> : <User className="h-4 w-4 text-white" />}
            </div>
            <div className={`max-w-[80%] p-3 rounded-lg text-sm ${msg.role === 'assistant' ? 'bg-white border border-gray-200 text-gray-800 rounded-bl-none' : 'bg-primary text-white rounded-br-none'}`}>
              {msg.parts.map((p, i) => p.kind === 'text' ? <p key={i} className="whitespace-pre-wrap">{p.content}</p> : null)}
              {streaming && msg.id === lastMsg?.id && msg.role === 'assistant' && !msg.parts[0]?.content && (
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      <div className="px-5 py-4 border-t border-gray-200 bg-white shrink-0">
        <div className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
            placeholder="Ask about data quality, anomalies, patterns..." disabled={streaming}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50" />
          {streaming ? (
            <button onClick={() => abortRef.current?.abort()}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm">
              <Square className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={sendMessage} disabled={!input.trim()}
              className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primaryDark disabled:opacity-40 transition-colors flex items-center gap-1.5">
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">Enter to send</p>
      </div>
    </div>
  );
}

export default function Investigate() {
  const [investigations, setInvestigations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');

  const active = investigations.find(i => i.id === activeId);

  const newInvestigation = () => {
    const inv = { id: `inv-${Date.now()}`, title: `Investigation ${investigations.length + 1}`, status: 'open', severity: 'medium', createdAt: new Date().toISOString(), messages: [] };
    setInvestigations(prev => [inv, ...prev]);
    setActiveId(inv.id);
    setActiveTab('chat');
  };

  const updateActive = (updater) => {
    setInvestigations(prev => prev.map(inv => inv.id === activeId ? (typeof updater === 'function' ? updater(inv) : { ...inv, ...updater }) : inv));
  };

  const STATUSES = ['open', 'in_progress', 'resolved', 'escalated'];

  return (
    <div className="flex flex-col h-full">
      <p className="px-6 pt-4 pb-0 text-[0.8rem] italic text-gray-600">Track and investigate data quality issues</p>
      <div className="flex-1 p-4 overflow-hidden">
        <div className="flex h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-200 flex flex-col shrink-0">
        <div className="px-4 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Search className="h-5 w-5" /> Investigations
          </h2>
          <button onClick={newInvestigation}
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primaryDark transition-colors">
            <Plus className="h-4 w-4" /> New Investigation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {investigations.map(inv => (
            <button key={inv.id} onClick={() => setActiveId(inv.id)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                activeId === inv.id ? 'border-primary/30 bg-primaryLight' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}>
              <p className={`text-sm font-medium truncate mb-1.5 ${activeId === inv.id ? 'text-primary' : 'text-gray-800'}`}>{inv.title}</p>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${SEVERITY_BADGE[inv.severity] || SEVERITY_BADGE.medium}`}>{inv.severity}</span>
                <span className="text-[10px] text-gray-400">{inv.status.replace('_', ' ')}</span>
              </div>
            </button>
          ))}
          {!investigations.length && <p className="text-xs text-gray-400 px-1 py-2">No investigations yet.</p>}
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {active ? (
          <>
            {/* Header */}
            <div className="border-b border-gray-200 bg-white shrink-0">
              <div className="px-6 py-3 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <input value={active.title} onChange={e => updateActive({ title: e.target.value })}
                    className="text-lg font-bold text-gray-900 bg-transparent border-none outline-none" />
                  <p className="text-xs text-gray-400">{new Date(active.createdAt).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {STATUSES.map(s => (
                    <button key={s} onClick={() => updateActive({ status: s })}
                      className={`text-xs px-3 py-1.5 rounded-md border font-medium transition-colors ${
                        active.status === s ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}>{s.replace('_', ' ')}</button>
                  ))}
                </div>
              </div>
              {/* Tabs */}
              <div className="flex px-6">
                {[['chat', MessageSquare, 'Chat'], ['dq_rules', ShieldAlert, 'DQ Rules']].map(([key, Icon, label]) => (
                  <button key={key} onClick={() => setActiveTab(key)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === key ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-hidden bg-white">
              {activeTab === 'chat' && <InvestigationChat investigation={active} onUpdate={updateActive} />}
              {activeTab === 'dq_rules' && <div className="overflow-y-auto h-full"><DQRulesPanel sourceId={null} /></div>}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="p-5 bg-primaryLight rounded-full mb-5">
              <Search className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Select or create an investigation</h2>
            <p className="text-sm text-gray-500 max-w-md mb-6">Investigations let you explore data quality issues, anomalies, and schema drift with AI assistance.</p>
            <button onClick={newInvestigation}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primaryDark transition-colors">
              <Plus className="h-4 w-4" /> Start Investigation
            </button>
          </div>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
